import { Identifiable, List } from "@planzone/dto";
import { Boundaries } from "@planzone/core";
import { isSame } from "./utils/isSame";
import { shiftList, getShift, hasShifted } from "./utils/shiftList";
import { resizeList } from "./utils/resizeList";

function mergeLists(
  list: Identifiable<List>,
  nextList: List,
): Identifiable<List> {
  return {
    ...list,
    name: nextList.name,
    description: nextList.description,
    startDate: nextList.startDate,
    endDate: nextList.endDate,
    parentList: nextList.parentList,
    tags: nextList.tags,
    activityKeyRank: nextList.activityKeyRank,
    statusKeyRank: nextList.statusKeyRank,
    attributes: nextList.attributes,
  };
}

function editList(
  list: Identifiable<List>,
  nextList: Identifiable<List>,
): { hasMatch: boolean; list: Identifiable<List> } {
  return isSame(list, nextList)
    ? {
        hasMatch: true,
        list: mergeLists(
          list,
          hasShifted(list, nextList)
            ? shiftList(list, getShift(list, nextList))
            : nextList,
        ),
      }
    : list.subLists.reduce<ReturnType<typeof editList>>(
        ({ hasMatch, list }, subList) => {
          const { hasMatch: hasSubListMatch, list: nextSubList } = editList(
            subList,
            nextList,
          );
          const isMatching = hasMatch || hasSubListMatch;
          const l = { ...list, subLists: [...list.subLists, nextSubList] };

          return {
            list: isMatching ? resizeList(l, nextList.endDate) : l,
            hasMatch: isMatching,
          };
        },
        { list: { ...list, subLists: [] }, hasMatch: false },
      );
}

function handleEditRequest(
  list: Identifiable<List>,
  req: Boundaries["editList"]["request"],
): Identifiable<List> {
  return isSame(req.list, list)
    ? hasShifted(list, mergeLists(list, req.nextList))
      ? shiftList(
          mergeLists(list, req.nextList),
          getShift(list, mergeLists(list, req.nextList)),
        )
      : mergeLists(list, req.nextList)
    : {
        ...list,
        subLists: list.subLists.map((subList) =>
          handleEditRequest(subList, req),
        ),
      };
}

function handleEditSuccess(
  list: Identifiable<List>,
  editedList: Boundaries["editList"]["success"],
): Identifiable<List> {
  const modifiedElements =
    "modifiedElements" in editedList &&
    typeof editedList.modifiedElements === "object"
      ? Object.values(
          editedList.modifiedElements as { activities: Identifiable<List>[] },
        ).flat()
      : [];
  const foundModified = modifiedElements.find(({ uuid }) => uuid === list.uuid);

  return isSame(list, editedList)
    ? editList(list, editedList).list
    : !!foundModified
      ? {
          ...list,
          startDate: foundModified.startDate,
          endDate: foundModified.endDate,
        }
      : list;
}

function handleEditFailure(
  list: Identifiable<List>,
  req: Boundaries["editList"]["failure"],
): Identifiable<List> {
  return isSame(req.list, list)
    ? hasShifted(list, req.list)
      ? shiftList(req.list, getShift(mergeLists(list, req.nextList), list))
      : mergeLists(list, req.list)
    : {
        ...list,
        subLists: list.subLists.map((subList) =>
          handleEditFailure(subList, req),
        ),
      };
}

export { handleEditRequest, handleEditSuccess, handleEditFailure };
